import { FC, ReactNode } from 'react';

import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { getGiftAgentOrder } from '@shared/utils/giftAgentOrder.util';
import {
  GIFT_STORE_PREVIEW_PARAM,
  getGiftStorePreview,
  markGiftStorePreview,
} from '@shared/utils/giftStorePreview.util';
import { useLocation, useSearchParams } from 'react-router-dom';

import { GiftStoreGateNotice } from './GiftStoreGateNotice';
import { useGiftStoreAccess } from '../hooks/useGiftStoreAccess';

/**
 * 답례품 "구매" 진입점(카탈로그/상세/장바구니/결제)을 감싸는 게이트.
 * - bugoId 없음 → 부고 링크 안내
 * - bugoId는 있으나 부고 없음(만료/오타) → 유효하지 않은 링크 안내
 * - 발인 후 30일 초과 → 온라인 주문 마감 안내 (전화 주문 유도)
 * - 그 외 → children 정상 렌더
 *
 * 이 게이트가 useBugoHook을 호출하는 유일한 지점이 되도록, 자식 페이지는
 * 부고 데이터를 재조회하지 않고 이미 채워진 recoil atom을 읽어 쓴다.
 *
 * 주문내역(orders)·주문 상세 등 "조회" 경로는 마감 후에도 봐야 하므로 감싸지 않는다.
 */
const GiftStorePurchaseGate: FC<{ children: ReactNode }> = function GiftStorePurchaseGate({
  children,
}) {
  const [searchParams] = useSearchParams();
  const bugoId = searchParams.get('bugoId');
  const { pathname } = useLocation();
  const { isAdmin } = useAuth();
  const { access, deceasedName } = useGiftStoreAccess(bugoId);

  // 관리자 미리보기 진입(?preview=1) — 이후 스토어 내 이동에선 파라미터가 떨어지므로
  // 탭 단위로 표시를 남긴다. 렌더 중에 남겨야 자식의 첫 조회 로그(store_view)부터 막힌다.
  if (bugoId && searchParams.get(GIFT_STORE_PREVIEW_PARAM) === '1' && isAdmin()) {
    markGiftStorePreview(
      bugoId,
      searchParams.get('previewBrand') ?? undefined,
      searchParams.get('previewTest') === '1',
    );
  }
  const preview = getGiftStorePreview(bugoId);
  const isPreview = !!preview;

  if (access === 'loading') {
    return (
      <div className="center-box h-screen-15">
        <Loading />
      </div>
    );
  }

  if (access === 'no-bugo') {
    return <GiftStoreGateNotice variant="no-bugo" />;
  }

  // bugoId는 있으나 서버에 해당 부고가 없음(만료/오타/형식오류) → 안내
  if (access === 'invalid') {
    return <GiftStoreGateNotice variant="invalid" />;
  }

  if (isPreview && (access === 'open' || access === 'closed')) {
    // 빌려온 실제 상가로 주문이 귀속되면 안 되므로 결제 화면은 막는다
    // (상조에 지정된 테스트 상가로 연 경우엔 결제 테스트 허용)
    if (!preview?.test && pathname.endsWith('/checkout')) {
      return (
        <div className="center-box h-screen-15 px-6 text-center">
          <div>
            <p className="text-base font-semibold text-gray-900">
              미리보기에서는 결제할 수 없습니다
            </p>
            <p className="mt-2 text-sm text-gray-500">
              실제 상가 부고를 빌려 연 화면이라 주문은 막아 두었습니다. (테스트 상가 자동
              생성은 백엔드 배포 후 지원)
            </p>
          </div>
        </div>
      );
    }
    // 오래된 부고를 빌려도 열리도록 온라인 주문 마감(발인+30일)은 우회
    return <>{children}</>;
  }

  if (access === 'closed') {
    // 대리주문 모드(상주검색 → 관리자 진입)는 마감 이후 전화 주문 대응이 주 용도라
    // 온라인 마감을 우회한다.
    if (bugoId && getGiftAgentOrder(bugoId)) {
      return <>{children}</>;
    }
    return (
      <GiftStoreGateNotice variant="closed" deceasedName={deceasedName} bugoId={bugoId} />
    );
  }

  return <>{children}</>;
};

export default GiftStorePurchaseGate;
